/**
 * After a deploy, hashed Vite/SvelteKit files under `/_app/immutable/` are replaced.
 * Tabs still running the previous shell then dynamic-import stale URLs. Firebase Hosting's
 * SPA fallback (`**` → `/index.html`) often answers those requests with HTML, which browsers
 * surface as TypeErrors rather than 404s:
 *
 * - Safari: `'text/html' is not a valid JavaScript MIME type` (JAVASCRIPT-21P)
 * - Safari: `Importing a module script failed` (JAVASCRIPT-1ZC)
 * - Chrome: `Failed to fetch dynamically imported module: …/nodes/…` (JAVASCRIPT-21D)
 *
 * `app.html` auto-reloads on these messages. They are expected deploy noise, not application
 * bugs, so Sentry must not record them as errors. Successful recovery is reported at info via
 * `had-chunk-error` (JAVASCRIPT-2BV). Keep this list in sync with the inline matcher in `app.html`.
 */
export const STALE_CHUNK_ERROR_PATTERNS = [
  'is not a valid JavaScript MIME type',
  'Expected a JavaScript module script',
  'Failed to fetch dynamically imported module',
  'Importing a module script failed',
  'error loading dynamically imported module',
  'Unable to preload CSS',
] as const;

const RELOAD_KEY = 'chunk-load-error-reload';
const RECOVERY_FLAG_KEY = 'had-chunk-error';
const RELOAD_COOLDOWN_MS = 10_000;

/** Tag set when a stale-chunk error recurs inside the reload cooldown, i.e. reloading did not fix it. */
export const RELOAD_SUPPRESSED_TAG = 'stale_chunk_reload_suppressed';

export type StaleChunkSentryEvent = {
  message?: string;
  level?: string;
  tags?: Record<string, unknown>;
  exception?: { values?: Array<{ value?: string }> };
};

export type StaleChunkEventHint = {
  originalException?: unknown;
};

function getErrorMessage(error: unknown): string {
  if (error == null) return '';
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message || '';
  if (typeof error === 'object') {
    const value = error as {
      message?: unknown;
      reason?: unknown;
      error?: unknown;
    };
    if (typeof value.message === 'string' && value.message) return value.message;
    if (value.reason !== undefined) return getErrorMessage(value.reason);
    if (value.error !== undefined && value.error !== error) {
      return getErrorMessage(value.error);
    }
  }
  return '';
}

export function isStaleChunkMessage(message: string): boolean {
  return STALE_CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
}

export function isStaleChunkError(error: unknown): boolean {
  return isStaleChunkMessage(getErrorMessage(error));
}

export function isStaleChunkSentryEvent(event: StaleChunkSentryEvent): boolean {
  if (event.message && isStaleChunkMessage(event.message)) return true;
  return (event.exception?.values ?? []).some(
    (value) => typeof value.value === 'string' && isStaleChunkMessage(value.value),
  );
}

/** Drop expected stale-asset failures so they never become Sentry error issues. */
export function filterStaleChunkBeforeSend<T extends StaleChunkSentryEvent>(
  event: T,
  hint?: StaleChunkEventHint,
): T | null {
  // A repeat failure right after a reload is a real problem (broken deploy/CDN), not deploy noise.
  if (event.tags?.[RELOAD_SUPPRESSED_TAG]) return event;
  if (hint?.originalException && isStaleChunkError(hint.originalException)) {
    return null;
  }
  if (isStaleChunkSentryEvent(event)) {
    return null;
  }
  return event;
}

/** Returns true when a reload was triggered, false when suppressed by the cooldown. */
export function scheduleStaleChunkReload(): boolean {
  if (typeof window === 'undefined') return false;

  try {
    const lastReload = window.sessionStorage.getItem(RELOAD_KEY);
    const now = Date.now();
    if (!lastReload || now - parseInt(lastReload, 10) > RELOAD_COOLDOWN_MS) {
      window.sessionStorage.setItem(RELOAD_KEY, now.toString());
      window.sessionStorage.setItem(RECOVERY_FLAG_KEY, 'true');
      window.location.reload();
      return true;
    }
    return false;
  } catch {
    window.location.reload();
    return true;
  }
}
